/* MP CART — custom behaviour for sections/mp-cart.liquid.
   Stock assets/cart.js keeps handling quantity updates, removals and the
   order note. This file adds:
   1. refreshMPCart() — re-render the MP cart section's dynamic regions from
      a fresh server render (items, totals + discount chips, shipping bar,
      cross-sell strip, count chip).
   2. <mp-cart-upsell> — AJAX quick-add for the cross-sell cards (bundles the
      section re-render into the /cart/add call; never publishes the
      cartUpdate pub-sub event, which would make stock CartItems.onCartUpdate
      fetch the *stock* main-cart-items section into our custom markup).
   3. <mp-cart-discount> — apply/remove discount codes on the cart page via
      the Cart AJAX API (/cart/update.js `discount` field).
   4. An instance patch of the page's CartItems.onCartUpdate so foreign
      cartUpdate publishes re-render OUR section instead of the stock one.
   5. Header item-count chip + cross-sell refresh on cart:change (dispatched
      by stock cart.js after every quantity change/removal).
   The free-shipping bar is fully server-rendered inside .js-contents-shipping
   (see snippets/mp-cart-shipping-bar.liquid) — no JS needed here. */

(() => {
	// Loaded from both the header (drawer mode) and the mp-cart section — run once.
	if (window.__mpCartInit) return
	window.__mpCartInit = true

	// Diagnostic version stamp — bump when editing this file.
	window.__mpVersions = Object.assign({}, window.__mpVersions, { 'mp-cart.js': 'v17' })
	console.info('[MP cart] mp-cart.js v17 loaded')

	const parseSection = (html) => new DOMParser().parseFromString(html, 'text/html')

	const swapFromDoc = (doc, targetSelector, sourceSelector) => {
		const target = document.querySelector(targetSelector)
		const source = doc.querySelector(sourceSelector || targetSelector)
		if (target && source) target.innerHTML = source.innerHTML
	}

	const syncCountChip = (doc) => {
		const source = doc.querySelector('[data-mp-cart-count-wrap]')
		const target = document.querySelector('[data-mp-cart-count-wrap]')
		if (source && target) {
			target.innerHTML = source.innerHTML
			target.classList.toggle('hidden', source.classList.contains('hidden'))
		}
	}

	const sectionId = () =>
		document.querySelector('.mp-cart cart-items')?.dataset.source ||
		document.getElementById('main-cart-items')?.dataset.id

	/* Header cart bubble: #cart-icon-bubble holds the basket icon + label
	   markup that the cart-icon-bubble SECTION render does not include, so
	   its contents must never be wholesale-replaced (stock cart.js did, and
	   the icon vanished after every cart update). Update only the count.
	   Accepts either the fetched section HTML or a plain item count. */
	const updateBubbleCount = (source) => {
		const bubble = document.getElementById('cart-icon-bubble')
		if (!bubble) return
		let count = null
		let srText = null
		if (typeof source === 'number') {
			count = String(source)
			srText = `${source} ${source === 1 ? 'item' : 'items'}`
		} else if (typeof source === 'string') {
			const doc = parseSection(source)
			count = doc.querySelector('.cart-count-bubble span[aria-hidden="true"]')?.textContent.trim() ?? ''
			srText = doc.querySelector('.cart-count-bubble .visually-hidden')?.textContent.trim()
		}
		if (count === null) return
		const countEl = bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]')
		if (countEl) countEl.textContent = count
		const srEl = bubble.querySelector('.cart-count-bubble .visually-hidden')
		if (srEl && srText) srEl.textContent = srText
	}
	window.mpUpdateCartBubble = updateBubbleCount

	const applySectionDoc = (doc) => {
		swapFromDoc(doc, '#main-cart-items .js-contents', '.js-contents')
		swapFromDoc(doc, '#main-cart-footer .js-contents-totals', '.js-contents-totals')
		swapFromDoc(doc, '#main-cart-shipping .js-contents-shipping', '.js-contents-shipping')
		swapFromDoc(doc, '.js-contents-upsell', '.js-contents-upsell')
		syncCountChip(doc)
	}

	const refreshMPCart = () => {
		const id = sectionId()
		if (!id) return Promise.resolve()
		// Render against the CURRENT page, not /cart — the drawer section
		// intentionally renders empty in cart-page context.
		return fetch(`${window.location.pathname}?section_id=${id}`)
			.then((response) => response.text())
			.then((html) => applySectionDoc(parseSection(html)))
	}

	class MPCartUpsell extends HTMLElement {
		connectedCallback() {
			this.addEventListener('change', (event) => {
				// Dropdown option or swatch radio — both carry the same data attrs.
				let option = null
				const select = event.target.closest('.mp-upsell__variants')
				if (select) {
					option = select.selectedOptions[0]
				} else if (event.target.classList.contains('mp-upsell__swatch-input')) {
					option = event.target
				}
				const card = event.target.closest('.mp-upsell__card')
				if (!option || !card) return

				const addButton = card.querySelector('.mp-upsell__add')
				if (addButton) addButton.dataset.variantId = option.value

				const price = card.querySelector('[data-upsell-price]')
				if (price && option.dataset.price) price.textContent = option.dataset.price

				const compare = card.querySelector('[data-upsell-compare]')
				if (compare) {
					if (option.dataset.compare) {
						compare.textContent = option.dataset.compare
						compare.hidden = false
					} else {
						compare.textContent = ''
						compare.hidden = true
					}
				}

				// Reflect the selected variant in the card thumbnail.
				const media = card.querySelector('.mp-upsell__media img')
				if (media && option.dataset.image) {
					media.src = option.dataset.image
					media.srcset = `${option.dataset.image} 1x, ${option.dataset.image2x || option.dataset.image} 2x`
				}
			})

			this.addEventListener('click', (event) => {
				const button = event.target.closest('.mp-upsell__add')
				if (button) this.add(button)
			})
		}

		add(button) {
			const variantId = Number(button.dataset.variantId)
			if (!variantId || button.disabled) return

			button.disabled = true
			const label = button.querySelector('span')
			const originalText = label ? label.textContent : ''
			if (label) label.textContent = '…'

			const id = this.dataset.sectionId
			const sections = [id, 'cart-icon-bubble', 'cart-live-region-text']
			const body = JSON.stringify({
				items: [{ id: variantId, quantity: 1 }],
				sections,
				sections_url: window.location.pathname,
			})

			fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
				.then((response) => response.json())
				.then((data) => {
					if (data.status) {
						this.showError(data.description || window.cartStrings.error)
						return
					}

					applySectionDoc(parseSection(data.sections[id]))
					if (data.sections['cart-icon-bubble']) updateBubbleCount(data.sections['cart-icon-bubble'])

					const liveRegion = document.getElementById('cart-live-region-text')
					if (liveRegion && data.sections['cart-live-region-text']) {
						const liveSource = parseSection(data.sections['cart-live-region-text']).querySelector('.shopify-section')
						if (liveSource) liveRegion.innerHTML = liveSource.innerHTML
						liveRegion.setAttribute('aria-hidden', false)
						setTimeout(() => liveRegion.setAttribute('aria-hidden', true), 1000)
					}

				})
				.catch(() => {
					this.showError(window.cartStrings.error)
				})
				.finally(() => {
					// The card may have been re-rendered away; only restore if it survived.
					if (button.isConnected) {
						button.disabled = false
						if (label) label.textContent = originalText
					}
				})
		}

		showError(message) {
			const errors = document.getElementById('cart-errors')
			if (!errors) return
			errors.textContent = message
			clearTimeout(this.errorTimer)
			this.errorTimer = setTimeout(() => {
				errors.textContent = ''
			}, 5000)
		}
	}

	if (!customElements.get('mp-cart-upsell')) {
		customElements.define('mp-cart-upsell', MPCartUpsell)
	}

	class MPCartDiscount extends HTMLElement {
		connectedCallback() {
			this.input = this.querySelector('.mp-discount__input')
			this.applyButton = this.querySelector('.mp-discount__apply')
			this.errorEl = this.querySelector('.mp-discount__error')
			this.summaryRoot = this.closest('.mp-summary') || document

			if (this.applyButton) this.applyButton.addEventListener('click', () => this.apply())
			if (this.input) {
				this.input.addEventListener('keydown', (event) => {
					if (event.key === 'Enter') {
						event.preventDefault()
						this.apply()
					}
				})
			}
			// The applied-code chips live inside the re-rendered totals region,
			// so delegate removal clicks from the (stable) summary root.
			this.summaryRoot.addEventListener('click', (event) => {
				const removeButton = event.target.closest('[data-remove-code]')
				if (removeButton) this.remove(removeButton.dataset.removeCode)
			})

			// Liquid can only see codes that created a cart discount application;
			// shipping-only codes etc. need reconciling from the Ajax cart.
			fetch(`${routes.cart_url}.js`)
				.then((response) => response.json())
				.then((cart) => this.ensureChips(cart.discount_codes))
				.catch(() => {})
		}

		/* Append chips for applied codes the server render couldn't show
		   (codes with no cart-level application, e.g. free-shipping codes,
		   or codes that stopped applying after a cart change). */
		ensureChips(discountCodes) {
			const wrap = this.summaryRoot.querySelector('[data-mp-chips]')
			if (!wrap || !discountCodes) return
			// Scan the whole summary — codes with a cart-level application
			// already render as chips inside the discount rows.
			const shown = new Set(
				[...this.summaryRoot.querySelectorAll('[data-remove-code]')].map((button) =>
					button.dataset.removeCode.toLowerCase()
				)
			)
			discountCodes.forEach((dc) => {
				if (shown.has(dc.code.toLowerCase())) return
				const chip = document.createElement('span')
				chip.className = 'mp-tag mp-discount__chip' + (dc.applicable ? '' : ' mp-discount__chip--off')
				chip.setAttribute('role', 'listitem')
				if (!dc.applicable) chip.title = "This code doesn't apply to the current cart"

				const codeText = document.createTextNode(dc.code + ' ')
				const removeButton = document.createElement('button')
				removeButton.type = 'button'
				removeButton.className = 'mp-discount__remove focus-inset'
				removeButton.dataset.removeCode = dc.code
				removeButton.setAttribute('aria-label', `Remove discount code ${dc.code}`)
				removeButton.innerHTML =
					'<svg width="8" height="8" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'

				chip.append(codeText, removeButton)
				wrap.appendChild(chip)
				wrap.classList.remove('mp-discount__chips--empty')
			})
		}

		currentCodes() {
			return [...this.summaryRoot.querySelectorAll('[data-remove-code]')].map(
				(button) => button.dataset.removeCode
			)
		}

		setBusy(busy) {
			if (this.applyButton) this.applyButton.disabled = busy
			if (this.input) this.input.disabled = busy
		}

		showError(message) {
			if (!this.errorEl) return
			this.errorEl.textContent = message
			this.errorEl.hidden = false
		}

		hideError() {
			if (!this.errorEl) return
			this.errorEl.textContent = ''
			this.errorEl.hidden = true
		}

		update(codes) {
			const body = JSON.stringify({ discount: codes.join(',') })
			return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body }).then((response) =>
				response.json()
			)
		}

		apply() {
			if (!this.input) return
			const code = this.input.value.trim()
			if (!code) return

			const codes = this.currentCodes()
			if (codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
				this.showError('That code is already applied.')
				return
			}

			this.setBusy(true)
			this.update([...codes, code])
				.then((cart) => {
					const entry = (cart.discount_codes || []).find(
						(dc) => dc.code.toLowerCase() === code.toLowerCase()
					)
					if (entry && entry.applicable) {
						this.input.value = ''
						this.hideError()
						return refreshMPCart().then(() => this.ensureChips(cart.discount_codes))
					}
					// Unknown or not-applicable code: roll it back and explain.
					return this.update(codes).then(() => {
						this.showError("That code can't be applied to this cart.")
						return refreshMPCart()
					})
				})
				.catch(() => {
					this.showError(window.cartStrings.error)
				})
				.finally(() => {
					this.setBusy(false)
					if (this.input && !this.input.disabled) this.input.focus()
				})
		}

		remove(code) {
			const codes = this.currentCodes().filter((existing) => existing !== code)
			this.setBusy(true)
			this.hideError()
			this.update(codes)
				.then((cart) => refreshMPCart().then(() => this.ensureChips(cart.discount_codes)))
				.catch(() => {
					this.showError(window.cartStrings.error)
				})
				.finally(() => {
					this.setBusy(false)
				})
		}
	}

	if (!customElements.get('mp-cart-discount')) {
		customElements.define('mp-cart-discount', MPCartDiscount)
	}

	/* Stock CartItems.onCartUpdate re-fetches the *stock* main-cart-items
	   section (hardcoded section_id) and would replace our custom markup with
	   it if anything published a cartUpdate pub-sub event from a foreign
	   source (product form, app quick-add, ...). Re-point it at the MP
	   section on the page instead. Prototype-level patch so it covers every
	   instance — including drawer contents re-created by renderContents. */
	const patchOnCartUpdate = () => {
		const CartItemsClass = customElements.get('cart-items')
		if (!CartItemsClass || CartItemsClass.prototype.__mpPatched) return
		CartItemsClass.prototype.__mpPatched = true
		CartItemsClass.prototype.onCartUpdate = function () {
			return refreshMPCart().catch((e) => {
				console.error(e)
			})
		}
		/* Keep stock updateQuantity away from the header bubble — its swap
		   replaces #cart-icon-bubble's contents and deletes the basket icon.
		   The count updates via the cart:change listener below instead. */
		const originalGetSections = CartItemsClass.prototype.getSectionsToRender
		CartItemsClass.prototype.getSectionsToRender = function () {
			return originalGetSections.call(this).filter((section) => section.id !== 'cart-icon-bubble')
		}
	}
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', patchOnCartUpdate)
	} else {
		patchOnCartUpdate()
	}

	/* Merged split-line rows (data-merged-keys, see mp-cart-item.liquid):
	   quantity and remove must operate on ALL underlying cart lines, so
	   intercept those interactions in the capture phase before stock
	   cart.js's single-line handlers see them. The update collapses the
	   group into its first line (Shopify re-splits it as the bundle
	   discount requires) and re-renders from the bundled section. */
	/* Stock cart.js raises an in-flight guard while a quantity change is
	   pending — .cart__items--disabled on the container (cart.js:360-364) —
	   so a second click cannot land on a DOM whose data-index values are
	   about to change. This path deliberately bypasses stock cart.js
	   (capture-phase stopPropagation below), so it must raise that guard
	   itself. Without it, changing a MERGED row leaves every other row
	   clickable and positional, which is exactly the mixed cart this bug
	   lives in — boards plus split markers. Paired with the rule in
	   mp-cart.css keyed on this class. */
	const setCartBusy = (busy) => {
		const container =
			document.getElementById('main-cart-items') ||
			document.getElementById('CartDrawer-CartItems')
		if (container) container.classList.toggle('cart__items--disabled', busy)
		/* Mirrors the lock stock cart.js now applies (cart.js:140-150). The
		   container class only drives CSS, and pointer-events blocks the mouse
		   alone — the disabled ATTRIBUTE also stops keyboard activation and
		   element.click(), which is what actually prevents a second click firing
		   against a render that is about to be replaced. Scoped to the whole cart,
		   not just this row: the mis-addressing happens when the SECOND click
		   lands on a DIFFERENT row. */
		document.querySelectorAll('.mp-cart__table .quantity__button').forEach((button) => {
			if (busy) button.setAttribute('disabled', '')
			else button.removeAttribute('disabled')
		})
	}

	/* WHY THIS STAYS ON /cart/update.js — decided 2026-08-03, reversing the plan
	   in PLANS/MP_CART_KEY_ADDRESSING.md.

	   Shopify's docs say to prefer change.js for lines already in the cart,
	   because update.js ADDS a line when the identifier matches nothing, where
	   change.js errors. That reasoning is sound for a SINGLE line and it is why
	   the stock path above now uses change.js.

	   It does not transfer here. A merged row spans N keys and has to zero N-1 of
	   them and set the last — change.js changes ONE line per call, so that is N
	   sequential requests, and the BXGY discount recomputes after EVERY one. Keys
	   captured from a single render are all valid at request time; feed them in
	   one at a time and each call invalidates the keys the later calls depend on.
	   Migrating would manufacture the exact staleness this work exists to remove.

	   update.js applies the whole map atomically against one cart state, so all N
	   keys are evaluated together. Already key-addressed, so it cannot hit the
	   WRONG product — its failure mode is a duplicate line, not a mis-charge.
	   Narrowed further by the lock in setCartBusy above: the keys cannot go stale
	   between render and click if no second click can be fired. */
	const mergedUpdate = (row, quantity) => {
		const keys = (row.dataset.mergedKeys || '').split(',').filter(Boolean)
		if (!keys.length) return
		const updates = {}
		keys.slice(1).forEach((key) => {
			updates[key] = 0
		})
		updates[keys[0]] = quantity

		row.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.remove('hidden'))
		setCartBusy(true)
		const id = sectionId()
		const body = JSON.stringify({
			updates,
			sections: [id, 'cart-icon-bubble'],
			sections_url: window.location.pathname,
		})
		fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
			.then((response) => response.json())
			.then((cart) => {
				if (cart.sections && cart.sections[id]) {
					applySectionDoc(parseSection(cart.sections[id]))
				}
				if (cart.sections && cart.sections['cart-icon-bubble']) {
					updateBubbleCount(cart.sections['cart-icon-bubble'])
				}
				document.dispatchEvent(
					new CustomEvent('cart:change', {
						detail: { cart, sectionId: id },
					})
				)
			})
			.catch(() => {
				const errors = document.getElementById('cart-errors')
				if (errors) errors.textContent = window.cartStrings.error
			})
			/* .finally, not .then — mirrors stock cart.js:315. On success
			   applySectionDoc replaces .js-contents so this row is detached and
			   re-hiding its overlay is a harmless no-op; on FAILURE nothing
			   re-renders, and without this the row spun forever until a page
			   reload. The container survives the swap (applySectionDoc replaces
			   #main-cart-items .js-contents, not the container), so clearing the
			   busy class here reaches the right element either way. */
			.finally(() => {
				setCartBusy(false)
				row
					.querySelectorAll('.loading-overlay')
					.forEach((overlay) => overlay.classList.add('hidden'))
			})
	}

	document.addEventListener(
		'change',
		(event) => {
			const input = event.target.closest('.mp-row[data-merged-keys] .quantity__input')
			if (!input) return
			event.stopPropagation()
			const quantity = parseInt(input.value, 10)
			if (Number.isNaN(quantity) || quantity < 0) return
			mergedUpdate(input.closest('.mp-row'), quantity)
		},
		true
	)

	/* Enter in a quantity box must NOT submit #cart. Found by Quinn 2026-08-12.

	   The quantity inputs sit inside <form action="/cart" method="post" id="cart">
	   and the ONLY submit button associated with that form is Proceed to checkout
	   (mp-cart-summary.liquid:200 — the discount Apply is type="button"). HTML
	   implicit submission therefore makes Enter in a number field a click on
	   Checkout. Nothing guarded it.

	   On 0.7 that was a second door into the mis-addressing bug with no button
	   click at all: Enter posted the positional updates[] array AND went to
	   checkout in one keystroke. Removing `updates[]` already closed the
	   mis-charge, but Enter still jumped to checkout, which loses a quantity the
	   shopper had just typed — a symptom shaped like the bug we were fixing.

	   blur() rather than a synthetic change: blurring commits the value through
	   the native change path exactly once, so this cannot double-fire the AJAX
	   update. Same shape as the discount input's Enter guard above (~line 212).

	   Delegated, because the rows are replaced on every AJAX cart re-render and a
	   direct binding would not survive one. */
	document.addEventListener(
		'keydown',
		(event) => {
			if (event.key !== 'Enter') return
			if (!event.target.closest('.mp-cart__table .quantity__input')) return
			event.preventDefault()
			event.target.blur()
		},
		true
	)

	document.addEventListener(
		'click',
		(event) => {
			const removeButton = event.target.closest('.mp-row[data-merged-keys] cart-remove-button')
			if (!removeButton) return
			event.preventDefault()
			event.stopPropagation()
			mergedUpdate(removeButton.closest('.mp-row'), 0)
		},
		true
	)

	/* Keep the header count chip + cross-sell strip in sync with quantity
	   changes and removals handled by stock cart.js. */
	let upsellRefreshTimer
	document.addEventListener('cart:change', (event) => {
		const cart = event.detail && event.detail.cart
		if (!cart) return

		updateBubbleCount(cart.item_count)

		document.querySelectorAll('[data-mp-cart-count-wrap]').forEach((wrap) => {
			wrap.classList.toggle('hidden', cart.item_count === 0)
			const count = wrap.querySelector('[data-mp-cart-count]')
			if (count) count.textContent = cart.item_count
			const word = wrap.querySelector('[data-mp-count-word]')
			if (word) word.textContent = cart.item_count === 1 ? word.dataset.one : word.dataset.other
		})

		const upsell = document.querySelector('mp-cart-upsell')
		if (!upsell) return
		clearTimeout(upsellRefreshTimer)
		upsellRefreshTimer = setTimeout(() => {
			fetch(`${window.location.pathname}?section_id=${upsell.dataset.sectionId}`)
				.then((response) => response.text())
				.then((html) => {
					swapFromDoc(parseSection(html), '.js-contents-upsell', '.js-contents-upsell')
				})
				.catch(() => {})
		}, 400)
	})
})()
